import { useContext, useState, useEffect, useRef } from "react";
import {
  ListSubheader,
  List,
  Button,
  ListItem,
  Box,
  LinearProgress,
  Collapse,
  ListItemText,
  ListItemButton,
  alpha,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Chip,
  IconButton,
  StepContent,
  useTheme,
  styled
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { SidebarContext } from "src/contexts/SidebarContext";
import {
  AdminPanelSettings,
  CheckCircle,
  ExpandLess,
  ExpandMore,
  RadioButtonUnchecked,
  WorkspacePremiumTwoTone,
  Settings,
  MenuBookTwoTone,
} from "@mui/icons-material";
import ChromeReaderModeTwoToneIcon from "@mui/icons-material/ChromeReaderModeTwoTone";
import { FormattedMessage } from "react-intl";
import { AuthContext } from "src/contexts/JWTAuthContext";
import { SubMenuWrapper, MenuWrapper } from "./styles";
import { CourseProgressContext } from "src/contexts/CourseProgressContext";
import { LocalizationContext } from "src/App";

const StyledList = styled(List)(({ theme }) => ({
  padding: 0,
  '& .MuiListItemButton-root': {
    color: theme.colors.alpha.trueWhite[70],
    position: 'relative',
    '&:before': {
      content: '""',
      background:
        theme.palette.mode === 'dark'
          ? theme.colors.alpha.trueWhite[100]
          : theme.palette.primary.main,
      opacity: 0,
      transition: theme.transitions.create(['transform', 'opacity']),
      width: 6,
      height: 6,
      transform: 'scale(0)',
      transformOrigin: 'center',
      borderRadius: '20px',
      marginRight: theme.spacing(1.8),
    },
    '&:hover:before, &.active:before': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '&:hover, &.active': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.colors.alpha.trueWhite[100], 0.06)
          : alpha(theme.palette.primary.main, 0.12),
      borderRadius: theme.shape.borderRadius,
      color: theme.colors.alpha.trueWhite[100],
      '& .MuiSvgIcon-root': {
        color: theme.colors.alpha.trueWhite[100]
      }
    },
    '& .MuiSvgIcon-root': {
      color: theme.colors.alpha.trueWhite[70],
    }
  },
  '& .MuiListItemText-primary': {
    color: theme.colors.alpha.trueWhite[70],
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'normal',
    '&:hover, &.active': {
      color: theme.colors.alpha.trueWhite[100],
      '& .MuiSvgIcon-root': {
        color: theme.colors.alpha.trueWhite[100]
      }
    },
    '&.completed': {
      fontWeight: 'bold'
    }
  }
}));

function SidebarMenu({ onReady }: { onReady?: () => void }) {
  const { closeSidebar } = useContext(SidebarContext);
  const { role } = useContext(AuthContext);
  const {profileModulesMap, submodulesMap} = useContext(LocalizationContext);
  const { progress: courseProgress, loading } = useContext(CourseProgressContext);
  const [expandedModules, setExpandedModules] =
    useState<Record<string, boolean>>({} as Record<string, boolean>);
  const onReadyFiredRef = useRef(false);
  const theme = useTheme();

  useEffect(() => {
    const profile = courseProgress?.profile;
    if (!profile) return;
    // Wait for the localization maps to load; the effect re-runs when they arrive.
    if (Object.keys(profileModulesMap).length === 0 || Object.keys(submodulesMap).length === 0) return;
    const modules = profileModulesMap[profile] || [];
    const completedSubs = courseProgress?.visitedSubmodules as string[] || [];
    const initial: Record<string, boolean> = {};
    for (const mod of modules) {
      const moduleSubs = submodulesMap[mod] || [];
      const allCompleted = moduleSubs.length > 0 && moduleSubs.every(sub => completedSubs.includes(sub));
      initial[mod] = !allCompleted;
    }
    setExpandedModules(initial);
    if (!onReadyFiredRef.current) {
      onReadyFiredRef.current = true;
      onReady?.();
    }
  }, [courseProgress?.profile, profileModulesMap, submodulesMap]);

  const getProfileModules = (): string[] => {
      if (!courseProgress || !courseProgress.profile) return [];
      return profileModulesMap[courseProgress.profile] || [];
    };
  
    const getCompletedSubmodules = (): string[] => {
      if (!courseProgress || !courseProgress.visitedSubmodules) return []
      return courseProgress.visitedSubmodules as string[];
    };
  
const findActiveModule = (): {activeModule: string | null; activeSubmodule: string | null} => {
    const profileModules = getProfileModules();
    const completedSubs = getCompletedSubmodules();

    for (const module of profileModules) {
      const moduleSubs = submodulesMap[module] || [];
      const allSubsCompleted = moduleSubs.every(sub => completedSubs.includes(sub));

      if (!allSubsCompleted) {
        const activeSubmodule = moduleSubs.find(item => !completedSubs.includes(item));
        return { activeModule: module, activeSubmodule};
      }
    }
    return { activeModule: null, activeSubmodule: null};
  };


  const handleExpandClick = (module: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const { activeModule, activeSubmodule } = findActiveModule();
  const profileModules = getProfileModules();
  const completedSubmodules = getCompletedSubmodules();

  const renderSubmoduleList = (module: string) => {
    const currentSubmodules = submodulesMap[module] || [];

    return (
      <StyledList dense sx={{ py: 0 }}>
        {currentSubmodules.map((submodule) => {
          const isCompleted = completedSubmodules.includes(submodule);
          return (
            <ListItemButton
              key={submodule}
              component={NavLink}
              to={`/course/submodules/${submodule}`}
              sx={{ py: 0.5, pl: 0, width: 'fit-content', 
                alignSelf: 'flex-start',                
               }}
            >
              {isCompleted ? (
                <CheckCircle color="info" fontSize="small" sx={{ mr: 1 }} />
              ) : (
                <RadioButtonUnchecked color="disabled" fontSize="small" sx={{ mr: 1 }} />
              )}
              <ListItemText
                primary={<FormattedMessage id={`${submodule}Title`} />}

                primaryTypographyProps={{ variant: 'subtitle2', fontWeight: isCompleted ? 'bold' : 'normal' }}
              />
            </ListItemButton>
          );
        })}
      </StyledList>
    );
  };

  const renderStepper = () => {
    return (
      <Stepper orientation="vertical" sx={{ width: '100%' }}>
        {profileModules.map((module) => {
          const moduleSubs = submodulesMap[module] || [];
          const completedCount = moduleSubs.filter(sub =>
            completedSubmodules.includes(sub)
          ).length;
          const isComplete = completedCount === moduleSubs.length;
          const isActive = module === activeModule;
          const isExpanded = expandedModules[module];

          return (
            <Step
              key={module}
              expanded={isExpanded}
              active={isActive}
              completed={isComplete}
              sx={{
                '& .MuiStepLabel-root': {
                  '& .MuiStepLabel-label': {
                    color: theme.colors.alpha.trueWhite[70],
                    fontWeight: isActive ? 'bold' : 'normal',
                  },
                  '& .MuiStepIcon-root': {
                    color: isActive ? 'primary.main' :
                      isComplete ? 'success.main' : theme.colors.alpha.trueWhite[70]
                  }
                }
              }}
            >
              <StepLabel>
                  <Box
                    onClick={() => handleExpandClick(module)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      flexGrow: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: isActive
                        ? alpha(theme.palette.primary.main, 0.5)
                        : alpha(theme.colors.alpha.trueWhite[100], 0.08),
                      backgroundColor: isActive
                        ? alpha(theme.palette.primary.main, 0.18)
                        : alpha(theme.colors.alpha.trueWhite[100], 0.04),
                      transition: theme.transitions.create([
                        'background-color',
                        'border-color',
                      ]),
                      '&:hover': {
                        backgroundColor: isActive
                          ? alpha(theme.palette.primary.main, 0.26)
                          : alpha(theme.colors.alpha.trueWhite[100], 0.09),
                      },
                    }}
                  >
                    <Typography fontWeight={isActive ? 'bold' : 'normal'} fontSize={14}>
                      <FormattedMessage id={`${module}Title`} />
                    </Typography>
                    {isComplete && (
                      <Chip
                        label={<FormattedMessage id="completed" />}
                        size="small"
                        color="success"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {!isComplete && completedCount > 0 && (
                      <Chip
                        label={`${completedCount}/${moduleSubs.length}`}
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      sx={{ ml: 'auto', color: theme.colors.alpha.trueWhite[70] }}
                    >
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
              </StepLabel>
              <StepContent sx={{ pl: 0 }}>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {renderSubmoduleList(module)}
                  {isActive && !isComplete && <Box sx={{ mb: 2, mt: 2, ml: 2 }}>
                    <Button
                      component={NavLink}
                      to={`/course/submodules/${activeSubmodule}`}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      <FormattedMessage id={(courseProgress?.visitedSubmodules?.length ?? 0) === 0 ? 'startNow' : "resume"} /> {">>"}
                    </Button>
                  </Box>}
                </Collapse>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    );
  };

  if (loading) {
    return (
      <Box p={2}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <>
      <MenuWrapper sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}>

        <List component="div">
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={NavLink}
                  onClick={closeSidebar}
                  to="/dashboard"
                  startIcon={<ChromeReaderModeTwoToneIcon />}
                >
                  <FormattedMessage id="dashboard" />
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>

        {courseProgress?.osType && courseProgress?.profile && (
          <>
            <List
              component="div" subheader={
                <ListSubheader component="div" disableSticky>
                  <FormattedMessage id="progress" />
                </ListSubheader>
              }
            >

              <SubMenuWrapper>
              {renderStepper()}
              </SubMenuWrapper>
            </List>
          </>
        )}

        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              <FormattedMessage id="manage" />
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              {role === "root" && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={NavLink}
                    onClick={closeSidebar}
                    to="/admins/manage"
                    startIcon={<AdminPanelSettings />}
                  >
                    <FormattedMessage id="users" />
                  </Button>
                </ListItem>
              )}
              <ListItem component="div">
                <Button
                  disableRipple
                  component={NavLink}
                  onClick={closeSidebar}
                  to="/licenses"
                  startIcon={<WorkspacePremiumTwoTone />}
                >
                  <FormattedMessage id="licenses" />
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={NavLink}
                  onClick={closeSidebar}
                  to="/references"
                  startIcon={<MenuBookTwoTone />}
                >
                  <FormattedMessage id="references" />
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>

        {role === "root" && (
          <>
            <List
              component="div"
              subheader={
                <ListSubheader component="div" disableSticky>
                  <FormattedMessage id="settings" />
                </ListSubheader>
              }
            >
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={NavLink}
                      onClick={closeSidebar}
                      to="/settings"
                      startIcon={<Settings />}
                    >
                      <FormattedMessage id="general" />
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </List>
          </>
        )}
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;